import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import styles from '../../styles/common/AddressAutocomplete.module.css';
import { utilsService } from '../../services/utils.service';

interface AddressComponents {
  street: string;
  city: string;
  state: string;
  municipality: string;
  zipCode: string;
  fullAddress: string;
  iso2Code?: string;
  stateCode?: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: AddressComponents) => void;
  placeholder?: string;
  inputName?: string;
  required?: boolean;
  apiKey: string;
  inputClassName?: string;
  onLoadingChange?: (loading: boolean) => void;
  country?: string;
  disabled?: boolean;
  maxLength?: number;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Ingresa una dirección',
  inputName = 'address',
  required = false,
  apiKey,
  inputClassName = '',
  onLoadingChange,
  country,
  disabled = false,
  maxLength,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: ['places'],
  });

  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  }, [isLoaded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const extractAddressComponents = useCallback((place: google.maps.places.PlaceResult): AddressComponents => {
    let street = '';
    let city = '';
    let state = '';
    let municipality = '';
    let zipCode = '';
    let iso2Code = '';
    let stateCode = '';

    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;
        if (types.includes('street_number')) {
          street = component.long_name + ' ' + street;
        } else if (types.includes('route')) {
          street += component.long_name;
        } else if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_2')) {
          municipality = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.long_name;
          stateCode = component.short_name;
        } else if (types.includes('postal_code')) {
          zipCode = component.long_name;
        } else if (types.includes('country')) {
          iso2Code = component.short_name;
        }
      }
    }

    return {
      street: street.trim(),
      city,
      state,
      municipality,
      zipCode,
      fullAddress: place.formatted_address || street || '',
      iso2Code,
      stateCode,
    };
  }, []);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!isLoaded || !input.trim() || !autocompleteServiceRef.current) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    onLoadingChange?.(true);

    try {
      const requestOptions: google.maps.places.AutocompletionRequest = {
        input,
        types: ['address'],
      };

      if (country) {
        requestOptions.componentRestrictions = { country };
      }

      const result = await new Promise<google.maps.places.AutocompletePrediction[]>((resolve, reject) => {
        autocompleteServiceRef.current!.getPlacePredictions(
          requestOptions,
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([]);
            } else {
              reject(new Error(status));
            }
          }
        );
      });

      setSuggestions(result);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
      onLoadingChange?.(false);
    }
  }, [onLoadingChange, isLoaded, country]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setShowSuggestions(true);

    if (inputValue.length >= 3) {
      fetchSuggestions(inputValue);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ['address_components', 'formatted_address'],
      },
      async (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const addressData = extractAddressComponents(place);

          if (addressData.iso2Code && addressData.city && addressData.state && addressData.zipCode) {
            await utilsService.addLocationGoogle({
              ISO2Code: addressData.iso2Code,
              City: addressData.city,
              State: addressData.state,
              Zipcode: addressData.zipCode,
              StateCode: addressData.stateCode || undefined
            });
          }

          onChange(addressData.fullAddress);
          onAddressSelect(addressData);
          setShowSuggestions(false);
          setSuggestions([]);
        }
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} className={styles.container}>
      <input
        ref={inputRef}
        type="text"
        name={inputName}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value.length >= 3 && setShowSuggestions(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        disabled={disabled}
        maxLength={maxLength}
        className={`${styles.input} ${inputClassName}`}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#f8fafc' } : {}}
      />

      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <ul className={styles.suggestionsList}>
          {isLoading && (
            <li className={styles.loadingItem}>
              Cargando...
            </li>
          )}
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}
              className={styles.suggestionItem}
            >
              <div className={styles.suggestionContent}>
                <span>{suggestion.description}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
export type { AddressComponents };
